import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  UserCheck,
  DollarSign,
  FileText,
  Settings,
  X,
  TrendingUp,
  BookOpen,
  Calculator,
  Briefcase,
  CreditCard,
  FileSpreadsheet,
  BarChart3,
  ChevronDown,
  RefreshCw,
  ChevronRight,
  Tags,
  Receipt,
  Wallet,
  PieChart,
  CheckSquare,
  Calendar,
  Trash2,
  Building2,
  Image as ImageIcon,
  AlertTriangle,
  Eye,
  RotateCcw,
  Shield,
  Activity,
  MessageSquare,
  Lock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useModule } from '../../contexts/ModuleContext';
import { useBranding } from '../../contexts/BrandingContext';

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { activeModule } = useModule();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState([]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
    );
  };

  // Get branding config from global context
  const { config: branding } = useBranding();

  // Accounting paths set activeModule to 'accounting', but all accounting/expense/payroll/reports
  // nav groups belong to the retail sidebar (module: 'retail'). Treat them as the same.
  const effectiveModule = activeModule === 'accounting' ? 'retail' : activeModule || 'retail';

  const navigationGroups = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: LayoutDashboard,
      href: `/${effectiveModule}/dashboard`,
      roles: [
        'super_admin',
        'admin',
        'staff',
        'system_admin',
        'retail_admin',
        'branch_admin',
        'branch_staff'
      ],
      module: 'shared',
      standalone: true
    },
    {
      id: 'branches',
      name: 'Branches',
      icon: Building2,
      roles: ['super_admin', 'system_admin', 'retail_admin'],
      module: 'retail',
      children: [
        { name: 'All Branches', href: '/branches', icon: Building2 },
        {
          name: 'Analytics Overview',
          href: '/analytics/consolidated',
          icon: BarChart3,
          roles: ['super_admin', 'system_admin', 'retail_admin']
        }
      ]
    },
    {
      id: 'my-branch',
      name: 'My Branch',
      icon: Building2,
      roles: ['branch_admin'],
      module: 'retail',
      children: [
        { name: 'Branch Details', href: '/my-branch', icon: Building2 },
        { name: 'Branch Inventory', href: '/my-branch-inventory', icon: Package }
      ]
    },
    {
      id: 'inventory',
      name: 'Inventory',
      icon: Package,
      roles: [
        'super_admin',
        'admin',
        'staff',
        'system_admin',
        'retail_admin',
        'branch_admin',
        'branch_staff'
      ],
      module: 'retail',
      children: [
        { name: 'Catalogue', href: '/catalogue', icon: ImageIcon },
        { name: 'Products', href: '/products', icon: Package },
        {
          name: 'Inactive & Zero Stock',
          href: '/inactive-products',
          icon: AlertTriangle,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin', 'branch_admin']
        },
        {
          name: 'Branch Inventory',
          href: '/my-branch-inventory',
          icon: Package,
          roles: ['branch_staff']
        },
        {
          name: 'Stock Transfers',
          href: '/stock-transfers',
          icon: RefreshCw,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin', 'branch_admin']
        },
        {
          name: 'Stock Damages',
          href: '/stock-damages',
          icon: AlertTriangle,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        },
        {
          name: 'Categories',
          href: '/categories',
          icon: Tags,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        },
        {
          name: 'Accessory Categories',
          href: '/accessory-categories',
          icon: Package,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        }
      ]
    },
    {
      id: 'sales',
      name: 'Sales & Orders',
      icon: ShoppingCart,
      roles: [
        'super_admin',
        'admin',
        'staff',
        'system_admin',
        'retail_admin',
        'branch_admin',
        'branch_staff'
      ],
      module: 'retail',
      children: [
        { name: 'Sales', href: '/sales', icon: ShoppingCart },
        { name: 'Receipts', href: '/receipts', icon: Receipt },
        {
          name: 'Confirmations',
          href: '/sales/pending-confirmations',
          icon: CheckSquare,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin', 'branch_admin']
        },
        {
          name: 'Cancelled / Reversed',
          href: '/sales/cancelled',
          icon: RotateCcw,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin', 'branch_admin']
        },
        { name: 'WhatsApp History', href: '/receipts/whatsapp-history', icon: MessageSquare },
        {
          name: 'Analytics',
          href: '/sales/analytics',
          icon: TrendingUp,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin', 'branch_admin']
        }
      ]
    },
    {
      id: 'crm',
      name: 'CRM',
      icon: Users,
      roles: [
        'super_admin',
        'admin',
        'staff',
        'system_admin',
        'retail_admin',
        'branch_admin',
        'branch_staff'
      ],
      module: 'retail',
      children: [
        { name: 'Customers', href: '/customers', icon: Users },
        { name: 'Referrers', href: '/referrers', icon: UserCheck },
        {
          name: 'Commissions',
          href: '/commissions',
          icon: DollarSign,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin', 'branch_admin']
        },
        {
          name: 'Wholesalers',
          href: '/wholesalers',
          icon: Building2,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        }
      ]
    },
    {
      id: 'accounting',
      name: 'Accounting',
      icon: Calculator,
      roles: ['super_admin', 'admin', 'system_admin', 'retail_admin'],
      module: 'retail',
      children: [
        { name: 'Chart of Accounts', href: '/accounting/accounts', icon: BookOpen },
        { name: 'Cost Centers', href: '/accounting/cost-centers', icon: PieChart },
        { name: 'Fixed Assets', href: '/accounting/fixed-assets', icon: Building2 },
        { name: 'Bank Recon', href: '/accounting/reconciliation', icon: CheckSquare },
        { name: 'Journal Entries', href: '/accounting/journal-entries', icon: Calculator },
        { name: 'Vendors', href: '/accounting/vendors', icon: Briefcase },
        { name: 'Bills (AP)', href: '/accounting/bills', icon: CreditCard },
        { name: 'Invoices (AR)', href: '/accounting/invoices', icon: FileSpreadsheet }
      ]
    },
    {
      id: 'expenses',
      name: 'Expenses',
      icon: Wallet,
      roles: [
        'super_admin',
        'admin',
        'staff',
        'system_admin',
        'retail_admin',
        'branch_admin',
        'branch_staff'
      ],
      module: 'retail',
      children: [
        { name: 'My Expenses', href: '/expenses', icon: Wallet },
        {
          name: 'Budgets',
          href: '/budgets',
          icon: PieChart,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        },
        {
          name: 'Analytics',
          href: '/expenses/analytics',
          icon: BarChart3,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin', 'branch_admin']
        },
        {
          name: 'Categories',
          href: '/expense-categories',
          icon: Tags,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        }
      ]
    },
    {
      id: 'tasks',
      name: 'Task Management',
      icon: CheckSquare,
      roles: [
        'super_admin',
        'admin',
        'staff',
        'system_admin',
        'retail_admin',
        'branch_admin',
        'branch_staff'
      ],
      module: 'shared',
      children: [
        {
          name: 'Performance Tasks',
          href: '/performance-tasks/active',
          icon: Activity,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        },
        { name: 'My Tasks', href: '/tasks', icon: CheckSquare },
        { name: 'Calendar View', href: '/tasks/calendar', icon: Calendar },
        { name: 'My Performance', href: '/my-performance-tasks', icon: TrendingUp },
        {
          name: 'Performance History',
          href: '/performance-tasks/inactive',
          icon: RotateCcw,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        }
      ]
    },
    {
      id: 'calculator',
      name: 'Tile Calculator',
      icon: Calculator,
      href: '/calculator',
      roles: [
        'super_admin',
        'admin',
        'staff',
        'system_admin',
        'retail_admin',
        'branch_admin',
        'branch_staff'
      ],
      module: 'retail',
      standalone: true
    },
    {
      id: 'payroll',
      name: 'Payroll',
      icon: Wallet,
      roles: ['super_admin', 'retail_admin', 'system_admin', 'branch_admin', 'staff', 'admin'],
      module: 'retail',
      children: [
        {
          name: 'Dashboard',
          href: '/payroll',
          icon: LayoutDashboard,
          roles: ['super_admin', 'system_admin', 'retail_admin']
        },
        {
          name: 'Salaries',
          href: '/payroll/salaries',
          icon: Users,
          roles: ['super_admin', 'system_admin', 'retail_admin']
        },
        {
          name: 'Payroll Runs',
          href: '/payroll/runs',
          icon: Calendar,
          roles: ['super_admin', 'system_admin', 'retail_admin']
        },
        { name: 'Loans', href: '/payroll/loans', icon: CreditCard },
        { name: 'Payslips', href: '/payroll/payslips', icon: FileText }
      ]
    },
    {
      id: 'reports',
      name: 'Financial Reports',
      icon: BarChart3,
      roles: ['super_admin', 'system_admin', 'admin'],
      module: 'retail',
      children: [
        { name: 'Statement of Account', href: '/reports/statement-of-account', icon: FileText },
        { name: 'Reports Hub', href: '/reports', icon: BarChart3 },
        { name: 'Sales Book', href: '/reports/sales-book', icon: BookOpen },
        { name: 'Consolidated P&L', href: '/reports/pl-consolidated', icon: TrendingUp },
        { name: 'Branch P&L', href: '/reports/pl-branch', icon: Building2 },
        { name: 'Inventory Value', href: '/reports/inventory-valuation', icon: Package }
      ]
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Settings,
      roles: ['super_admin', 'admin', 'system_admin', 'retail_admin'],
      module: 'shared',
      children: [
        {
          name: 'Branding',
          href: '/settings/branding',
          icon: Building2,
          roles: ['super_admin', 'admin', 'system_admin']
        },
        {
          name: 'Bank Channels',
          href: '/settings/bank-channels',
          icon: Building2,
          roles: ['super_admin', 'admin', 'system_admin']
        },
        {
          name: 'Fiscal Periods',
          href: '/settings/fiscal-periods',
          icon: Calendar,
          roles: ['super_admin', 'admin', 'system_admin']
        },
        {
          name: 'Tax Settings',
          href: '/settings/tax',
          icon: Calculator,
          roles: ['super_admin', 'admin', 'system_admin']
        },
        { name: 'Receipt Settings', href: '/receipts/settings', icon: Receipt },
        {
          name: 'WhatsApp Config',
          href: '/settings/whatsapp',
          icon: MessageSquare,
          roles: ['super_admin', 'admin', 'system_admin', 'retail_admin']
        },
        { name: 'Users', href: '/users', icon: Users },
        {
          name: 'Account Settings',
          href: '/settings/account',
          icon: Shield,
          roles: ['super_admin', 'system_admin']
        },
        {
          name: 'Security',
          href: '/settings/security',
          icon: Lock,
          roles: ['super_admin', 'system_admin']
        },
        {
          name: 'Demo Settings',
          href: '/settings/demo',
          icon: Eye,
          roles: ['super_admin', 'system_admin']
        },
        { name: 'Recycle Bin', href: '/recycle-bin', icon: Trash2 },
        {
          name: 'Purge Management',
          href: '/purge-management',
          icon: Settings,
          roles: ['super_admin', 'admin', 'system_admin']
        }
      ]
    }
  ];

  const filteredGroups = navigationGroups.filter(
    (group) =>
      group.roles.includes(user?.role) &&
      (group.module === 'shared' || group.module === effectiveModule)
  );

  const isActive = (href) => location.pathname === href;
  const isGroupActive = (group) => {
    if (group.standalone && group.href) {
      return isActive(group.href);
    }
    if (group.children) {
      return group.children.some((child) => isActive(child.href));
    }
    return false;
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 w-64 bg-[var(--retail-primary)] transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-[var(--retail-border)] flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Dynamic branding based on logo_type */}
          {branding.logo_type === 'none' ? (
            // Text only mode
            <span
              className={`text-[var(--retail-text)] font-bold leading-tight break-words ${
                branding.sidebar_name_size === 'small'
                  ? 'text-base'
                  : branding.sidebar_name_size === 'large'
                    ? 'text-2xl'
                    : 'text-xl'
              }`}
            >
              {branding.company_name}
            </span>
          ) : branding.logo_type === 'full' && branding.logo_url ? (
            // Full logo mode (combined logo with company name)
            <img
              src={branding.logo_url}
              alt={branding.company_name}
              className="h-10 max-w-full object-contain"
            />
          ) : (
            // Icon + text mode (default)
            <>
              <div className="w-10 h-10 bg-[var(--retail-accent)] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-primary-black font-bold text-sm">
                    {branding.logo_icon_text || 'BMS'}
                  </span>
                )}
              </div>
              <span
                className={`text-[var(--retail-text)] font-bold leading-tight break-words ${
                  branding.sidebar_name_size === 'small'
                    ? 'text-base'
                    : branding.sidebar_name_size === 'large'
                      ? 'text-2xl'
                      : 'text-xl'
                }`}
              >
                {branding.company_name}
              </span>
            </>
          )}
        </div>
        <button
          onClick={onClose}
          className="lg:hidden text-white hover:text-[var(--retail-accent)]"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 pb-24 space-y-1 overflow-y-auto overflow-x-hidden">
        {filteredGroups.map((group) => {
          const Icon = group.icon;
          const isExpanded = expandedSections.includes(group.id);
          const groupActive = isGroupActive(group);

          // Standalone item (like Dashboard)
          if (group.standalone) {
            return (
              <Link
                key={group.id}
                to={group.href}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive(group.href)
                    ? 'bg-[var(--retail-accent)] text-primary-black font-semibold'
                    : 'text-[var(--retail-text)] hover:bg-[var(--retail-hover)]'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{group.name}</span>
              </Link>
            );
          }

          // Group with children
          return (
            <div key={group.id}>
              {/* Group Header */}
              <button
                onClick={() => toggleSection(group.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                  groupActive
                    ? 'bg-[var(--retail-hover)] text-[var(--retail-accent)]'
                    : 'text-[var(--retail-text)] hover:bg-[var(--retail-hover)]'
                }`}
              >
                <div className="flex items-center gap-3 text-left">
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium text-left">{group.name}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {/* Group Children */}
              {isExpanded && group.children && (
                <div className="ml-4 mt-1 space-y-1">
                  {group.children
                    .filter((child) => !child.roles || child.roles.includes(user?.role))
                    .map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={onClose}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm ${
                            isActive(child.href)
                              ? 'bg-[var(--retail-accent)] text-primary-black font-semibold'
                              : 'text-[var(--retail-text)] opacity-80 hover:opacity-100 hover:bg-[var(--retail-hover)] hover:text-[var(--retail-text)]'
                          }`}
                        >
                          <ChildIcon className="h-4 w-4" />
                          <span>{child.name}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[var(--retail-border)] bg-[var(--retail-primary)] flex-shrink-0">
        <div className="flex items-center gap-3 px-3 py-2 bg-[var(--retail-hover)] rounded-lg">
          <div className="w-10 h-10 bg-[var(--retail-accent)] rounded-full flex items-center justify-center text-primary-black font-bold">
            {user?.full_name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[var(--retail-text)] text-sm font-medium truncate">
              {user?.full_name}
            </p>
            <p className="text-[var(--retail-text)] opacity-60 text-xs truncate">
              {user?.role === 'super_admin'
                ? 'Super Admin'
                : user?.role === 'system_admin'
                  ? 'System Admin'
                  : user?.role === 'retail_admin'
                    ? 'Retail Admin'
                    : user?.role === 'branch_admin'
                      ? 'Branch Admin'
                      : user?.role === 'branch_staff'
                        ? 'Branch Staff'
                        : user?.role === 'admin'
                          ? 'Admin'
                          : 'Staff'}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
